import React from "react";
import {LoanData} from "../loan-details/model/LoanData";
import {PaymentState} from "./model/PaymentState";
import {PostPayment} from "./component/post-payment/PostPayment";
import {PaymentResult} from "../../stripe/model/PaymentResult";
import {PaymentStatus} from "./service/PaymentService";
import {WebSocketServiceSingleton} from "../../websocket/service/WebSocketService";
import {WebSocketTopic} from "../../websocket/model/WebSocketTopic";
import {MessageData} from "../../websocket/model/MessageData";
import {PaymentInfo} from "./model/PaymentInfo";
import {EventTypeHolder} from "../../util/EventUtil";
import {PrePaymentMethodSwitch} from "./component/pre-payment/pre-payment-method-switch/PrePaymentMethodSwitch";
import {TileableComponent} from "../../app/tileable/TileableComponent";
import {PaymentInstructions} from "./component/instructions/PaymentInstructions";
import {PaymentMethod} from "./model/PaymentMethod";
import {EventRegistry} from "../../app/event-aware/EventRegistry";
import {RedirectType} from "../error/model/RedirectType";
import {BrowserServiceSingleton, SessionServiceSingleton, StripePingServiceSingleton, SnackbarServiceSingleton} from "src/Context";
import {ProcessingPayment} from "./component/processing-payment/ProcessingPayment";

interface Props {
}

interface State {
    loading: boolean;
    paymentInfo: PaymentInfo;
    loan: LoanData;
}

export class PaymentStateSwitch extends React.Component<Props, State> {

    private readonly browserService = BrowserServiceSingleton;
    private readonly wsService = WebSocketServiceSingleton;
    private readonly sessionService = SessionServiceSingleton;
    private readonly stripePingService = StripePingServiceSingleton;
    private readonly snackbarService = SnackbarServiceSingleton;
    private readonly eventRegistry = new EventRegistry();

    constructor(p: Props) {
        super(p);
        let loan = this.sessionService.getLoan();
        let info = this.sessionService.getPaymentInfo();
        let loading = this.isLoading(info);
        this.state = {
            loading: loading,
            loan: loan,
            paymentInfo: info,
        }
    }


    private isLoading = (info: PaymentInfo) => {
        let state = info.status.state;
        let isPrePayment = state === PaymentState.PRE_PAYMENT;
        let isCreditCard = info.method === PaymentMethod.CREDIT_CARD;
        return isPrePayment && isCreditCard;
    };

    componentDidMount() {
        this.wsService.activate();
        this.wsService.startSession(WebSocketTopic.PAYMENT_STATUS, this.handlePayment)
        this.eventRegistry.pushAll([
            {
                name: EventTypeHolder.stripeCardMounted,
                callback: () => {
                    this.setState({
                        loading: false
                    })
                }
            },
            {
                name: EventTypeHolder.paymentRequestSent,
                callback: () => {
                    this.setState({
                        loading: true
                    })
                }
            },
            {
                name: EventTypeHolder.paymentRequestComplete,
                callback: () => {
                    const current = this.state.paymentInfo;
                    const info: PaymentInfo = {
                        ...current,
                        status: {
                            ...current.status,
                            state: PaymentState.IN_PAYMENT,
                            result: null
                        }
                    };
                    this.sessionService.storePaymentInfo(info);
                    this.setState({
                        loading: false,
                        paymentInfo: info
                    });
                }
            },
            {
                name: EventTypeHolder.paymentRequestComplete,
                callback: () => {
                    console.log('initiating delayed ping...');
                    return this.stripePingService.delayedPing();
                }


            },
            {
                name: EventTypeHolder.paymentFailure,
                callback: () => {
                    this.setState({
                        loading: false
                    })
                }
            },
            {
                name: EventTypeHolder.inPaymentPing,
                callback: (evt: CustomEvent<PaymentStatus | null>) => this.onPaymentStatusUpdate(evt.detail)
            }
        ]);

        this.handleStripeReturn();
    }

    instructions = () => {
        let state = this.state.paymentInfo.status.state;
        let loan = this.state.loan;
        let method = this.state.paymentInfo.method;
        const selectedFees = this.sessionService.getSelectedFeesSafe();
        return <PaymentInstructions
            loan={loan}
            method={method}
            state={state}
            selectedFees={selectedFees}/>
    }

    render() {
        return (
            <TileableComponent instructions={this.instructions()}
                               loaded={!this.state.loading}
                               keepChildren={true}>
                {this.switchPaymentState()}
            </TileableComponent>
        );
    }

    private handlePayment = (message: MessageData<PaymentStatus>) => {
        let status = message.data;
        this.onPaymentStatusUpdate(status);
    }

    private onPaymentStatusUpdate = (status: PaymentStatus | null | undefined) => {
        if (!status) {
            return;
        }
        this.handleSingleResult(status.result);
        this.handlePaymentEnd(status);
    };

    private handleStripeReturn = () => {
        const isStripeReturn = this.hasStripeReturnParams();
        if (!isStripeReturn) return;
        const params = this.browserService.readUrlParams();
        const redirectStatus = params.get('redirect_status');
        this.browserService.removeUrlParams();

        if (redirectStatus === 'failed' || redirectStatus === 'canceled') {
            this.snackbarService.error('We could not verify your bank account. Please try again or use a different account.');
        }

        if (this.state.paymentInfo?.status?.state === PaymentState.POST_PAYMENT && this.state.paymentInfo.status.result) {
            return;
        }

        const info = this.state.paymentInfo;
        info.status.state = PaymentState.IN_PAYMENT;
        this.sessionService.storePaymentInfo(info);
        this.setState({
            loading: false,
            paymentInfo: info
        });

        this.stripePingService.ping()
            .then(status => {
                if (status) {
                    this.onPaymentStatusUpdate(status);
                } else {
                    this.setState({loading: false});
                    return this.stripePingService.delayedPing();
                }
            })
            .catch(err => {
                console.log(err);
                this.setState({loading: false});
            });
    };

    private hasStripeReturnParams = () => {
        const params = this.browserService.readUrlParams();
        return params.has('payment_intent')
            || params.has('payment_intent_client_secret')
            || params.has('redirect_status');
    };

    private handleSingleResult = (result: PaymentResult | null) => {
        if (!result) return;
        if (result.paid === true && result.receiptUrl) {
            const alreadyHadSuccess = this.state.paymentInfo?.status?.result?.paid === true;
            if (!alreadyHadSuccess) {
                this.browserService.openNewTab(result.receiptUrl);
            }
            return;
        }
        if (result.paid === false) {
            let errorParams = result.errorParams;
            if (errorParams) {
                if (errorParams.redirectType !== RedirectType.PAYMENT_CC_PAGE) {
                    this.browserService.redirectOnParams(errorParams);
                }
            }
        }
    };

    private handlePaymentEnd = (status: PaymentStatus) => {
        let info = this.assignPaymentInfo(status);
        this.sessionService.storePaymentInfo(info);
        this.setState({
            paymentInfo: info,
            loading: false
        });
    };

    private assignPaymentInfo = (status: PaymentStatus): PaymentInfo => {
        let info = this.state.paymentInfo;
        Object.assign(info?.status, {
            state: status.state,
            result: status.result
        })
        return info as PaymentInfo;
    };

    private switchPaymentState = () => {
        let state = this.state.paymentInfo?.status.state;
        switch (state) {
            case PaymentState.PRE_PAYMENT:
                return this.getPrePaymentElement();
            case PaymentState.POST_PAYMENT:
                return this.getPostPaymentElement();
            case PaymentState.IN_PAYMENT:
                return this.getInPaymentElement();
            default:
                return <></>;
        }

    };

    private getPrePaymentElement = () => {
        const selectedFees = this.sessionService.getSelectedFeesSafe();
        return <PrePaymentMethodSwitch
            disabled={this.state.loading}
            info={this.state.paymentInfo as PaymentInfo}
            loan={this.state.loan as LoanData}
            selectedFees={selectedFees}>
        </PrePaymentMethodSwitch>
    };


    private getPostPaymentElement = () => {
        let result = this.state.paymentInfo.status.result;
        let method = this.state.paymentInfo.method;
        let loan = this.state.loan;

        if (!result || this.isPendingResult(result)) {
            return <ProcessingPayment loan={loan}/>
        }

        return <PostPayment
            result={result}
            method={method}
            loan={loan}/>;
    }

    private getInPaymentElement = () => {
        return <ProcessingPayment loan={this.state.loan}/>
    };

    private isPendingResult = (result: PaymentResult): boolean => {
        if (result.paid === null) {
            return true;
        }
        const hasFailureMessage = result.failureMessage != null && result.failureMessage.length > 0;
        const hasFailureCode = result.failureCode != null && result.failureCode.length > 0;
        const hasErrorParams = result.errorParams != null;
        return !result.paid && !hasFailureMessage && !hasFailureCode && !hasErrorParams;
    };

    componentWillUnmount() {
        this.eventRegistry.clear();
        this.wsService.stopSession(WebSocketTopic.PAYMENT_STATUS);
        this.wsService.deactivate();
    }

}
